import { Loading, ModalError, PlantForm } from '@components';
import { validateInputImage } from '@helpers';
import { useErrorModal, useSavePlantDetail } from '@hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import type { PlantDetailFormType } from '@schema';
import { plantDetailFormSchema } from '@schema';
import { Plant } from '@types';
import { FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

type PlantFormWrapperProps = {
  plant: Plant;
  isAllowUpdateStatus?: boolean;
};

const PlantFormWrapper: FC<PlantFormWrapperProps> = ({ plant, isAllowUpdateStatus }) => {
  const { isOpen, content, setErrorContent, toggleModal } = useErrorModal();
  const { mutateAsync: savePlantDetail, isLoading: isSaving } = useSavePlantDetail();

  const methods = useForm<PlantDetailFormType>({
    defaultValues: {
      image: plant.image_base64,
      co2: plant.co2.toString(),
      reimbursement: plant.currency.toString(),
      ...(isAllowUpdateStatus && { isActive: plant.is_active }),
    },
    mode: 'onSubmit',
    resolver: zodResolver(plantDetailFormSchema),
    reValidateMode: 'onSubmit',
  });

  const { isSubmitting } = methods.formState;

  const onValidateInputImage = (e) => {
    const file = e.target.files[0];
    if (!e || !file) return;

    const result = validateInputImage(e);

    if (result.isError) {
      methods.setValue('image', null);
      setErrorContent(result.errorMessage);
      return;
    }

    methods.setValue('image', file);
  };

  const handleSubmit = methods.handleSubmit(async (formData) => {
    if(isSubmitting || isSaving) return;

    try {
      await savePlantDetail({ plant, formData });
    } catch {
      setErrorContent('Failed to save plant detail. Please try again.');
    }
  });

  const onClearImage = () => methods.setValue('image', null);

  useEffect(() => {
    methods.reset({
      image: plant.image_base64,
      co2: plant.co2.toString(),
      reimbursement: plant.currency.toString(),
      isActive: plant.is_active,
    });
  }, [plant, methods]);

  return (
    <FormProvider {...methods}>
      {(isSubmitting || isSaving) && <Loading />}
      <form>
        <PlantForm
          plant={plant}
          onClearImage={onClearImage}
          handleSubmit={handleSubmit}
          validateInputImage={onValidateInputImage}
          isAllowUpdateStatus={isAllowUpdateStatus}
        />
      </form>
      {isOpen && <ModalError isDisplay={isOpen} content={content} closeModal={toggleModal} />}
    </FormProvider>
  );
};

export default PlantFormWrapper;
